import { reportClientOperationBatch } from '../../graphql/mutations/clientReport';
import { getClientReporter } from './clientReporterRegistry';
import { ClientReportOpType } from './clientReportTypes';
import { CLIENT_REPORT_MAX_LEN_MAP, sanitizeAndTruncateForClientReport } from './clientReportUtils';
import { getUserInfo } from '../auth';

let started = false;
let pollTimerId: number | null = null;
let bootstrapTimerId: number | null = null;

function hasHubPdaPermission(): boolean {
  const role = getUserInfo()?.account?.role?.name;
  return role === 'HUB_MANAGER' || role === 'HUB_OPERATOR' || role === 'RETURNS_STAFF';
}

function startPolling(opTypes: string[]): void {
  if (started) return;
  started = true;

  const runOnce = async () => {
    for (const opType of opTypes) {
      const reporter = getClientReporter(opType);
      try {
        reporter.tickSwitchCounter();
        const switchCounter = reporter.getSwitchCounter();
        console.log(`[HubPDAClientReport] Polling(${opType}): switchCounter=${switchCounter}`);

        if (switchCounter > 0) {
          console.log(`[HubPDAClientReport] Polling(${opType}): skip, switchCounter > 0`);
          continue;
        }

        const records = await reporter.listReadyBatch(200, 60 * 1000);
        console.log(`[HubPDAClientReport] Polling(${opType}): records=${records.length}`);

        if (records.length === 0) {
          console.log(
            `[HubPDAClientReport] Polling(${opType}): no data older than 1 minute, skipping request`
          );
          continue;
        }

        // 发送用的 reportKey（清洗截断后）-> 原始 reportKey 列表（多条可能截断成同一条）
        const sentKeyToOriginalKeys: Record<string, string[]> = {};
        const items = records
          .map(r => {
            const sentReportKey = sanitizeAndTruncateForClientReport(r.reportKey, CLIENT_REPORT_MAX_LEN_MAP.reportKey);
            if (!sentKeyToOriginalKeys[sentReportKey]) {
              sentKeyToOriginalKeys[sentReportKey] = [];
            }
            sentKeyToOriginalKeys[sentReportKey].push(r.reportKey);
            return {
              reportKey: sentReportKey,
              opType: sanitizeAndTruncateForClientReport(r.opType, CLIENT_REPORT_MAX_LEN_MAP.opType),
              barCode: r.barCode != null ? sanitizeAndTruncateForClientReport(r.barCode, CLIENT_REPORT_MAX_LEN_MAP.barCode) : '',
              deviceId: r.deviceId != null ? sanitizeAndTruncateForClientReport(r.deviceId, CLIENT_REPORT_MAX_LEN_MAP.deviceId) : undefined,
              clientIp: r.clientIp != null ? sanitizeAndTruncateForClientReport(r.clientIp, CLIENT_REPORT_MAX_LEN_MAP.clientIp) : undefined,
              createdAtMs: r.createdAtMs,
              touchAtMs: r.touchAtMs,
              interceptAtMs: r.interceptAtMs,
              interceptReason: r.interceptReason,
              requestAtMs: r.requestAtMs,
              requestParamsJson: r.requestParamsJson,
              requestHeadersJson: r.requestHeadersJson,
              responseAtMs: r.responseAtMs,
              responseResult: r.responseResult
            };
          })
          .filter(item => item.reportKey.length > 0);

        const result = await reportClientOperationBatch({
          opType,
          items
        });

        console.log(
          `[HubPDAClientReport] Polling(${opType}): accepted=${result.acceptedKeys?.length || 0}, failed=${result.failedKeys?.length || 0}, switchValue=${result.switchValue || 0}`
        );

        if (result.switchValue !== 0) {
          reporter.setSwitchCounter(result.switchValue);
        }

        if (Array.isArray(result.acceptedKeys) && result.acceptedKeys.length > 0) {
          // 用服务端返回的 acceptedKeys（可能为清洗/截断后的 key）反查原始 key，按原始 key 删除，兼容历史数据
          const keysToDelete = result.acceptedKeys.flatMap((acceptedKey) => {
            const originals = sentKeyToOriginalKeys[acceptedKey];
            return originals?.length ? originals : [acceptedKey];
          });
          await reporter.deleteKeys(Array.from(new Set(keysToDelete)));
        }
      } catch (e) {
        console.error(`[HubPDAClientReport] Polling(${opType}) error:`, e);
        continue;
      }
    }
  };

  void runOnce();
  pollTimerId = window.setInterval(() => {
    void runOnce();
  }, 60 * 1000);
}

export function startHubPdaClientReportPollingManager(
  opTypes: string[] = [ClientReportOpType.SORTING_SCAN, ClientReportOpType.PICKUP_ARRIVAL_SCAN]
): void {
  if (started) return;

  if (hasHubPdaPermission()) {
    console.log('[HubPDAClientReport] Permission ok, starting polling manager.');
    startPolling(opTypes);
    return;
  }

  const userInfo = getUserInfo();
  if (userInfo) {
    console.log(
      `[HubPDAClientReport] Permission denied (role=${userInfo.account?.role?.name}), skip polling manager.`
    );
    return;
  }

  // 未登录时：短时间轮询等待登录完成（避免 App 启动早于登录导致永远不启动）
  console.log('[HubPDAClientReport] No user info yet, waiting for login...');
  const startAt = Date.now();
  const maxWaitMs = 60 * 1000;

  bootstrapTimerId = window.setInterval(() => {
    if (started) {
      if (bootstrapTimerId !== null) {
        window.clearInterval(bootstrapTimerId);
        bootstrapTimerId = null;
      }
      return;
    }

    const elapsed = Date.now() - startAt;
    if (elapsed > maxWaitMs) {
      console.log('[HubPDAClientReport] Wait login timeout, stop bootstrap.');
      if (bootstrapTimerId !== null) {
        window.clearInterval(bootstrapTimerId);
        bootstrapTimerId = null;
      }
      return;
    }

    if (hasHubPdaPermission()) {
      console.log('[HubPDAClientReport] Permission ok after login, starting polling manager.');
      if (bootstrapTimerId !== null) {
        window.clearInterval(bootstrapTimerId);
        bootstrapTimerId = null;
      }
      startPolling(opTypes);
      return;
    }

    const currentUserInfo = getUserInfo();
    if (currentUserInfo) {
      console.log(
        `[HubPDAClientReport] Logged in but permission denied (role=${currentUserInfo.account?.role?.name}), stop bootstrap.`
      );
      if (bootstrapTimerId !== null) {
        window.clearInterval(bootstrapTimerId);
        bootstrapTimerId = null;
      }
    }
  }, 2000);
}

export function stopHubPdaClientReportPollingManager(): void {
  if (pollTimerId !== null) {
    window.clearInterval(pollTimerId);
    pollTimerId = null;
  }
  if (bootstrapTimerId !== null) {
    window.clearInterval(bootstrapTimerId);
    bootstrapTimerId = null;
  }
  started = false;
}

